import { Button, Text, Tooltip, makeStyles, shorthands, tokens } from '@fluentui/react-components';
import { FC } from 'react';
import { useTranslation } from 'react-i18next';
import { QuestionFilled } from '@fluentui/react-icons';
import { appInsights } from '../../AppInsights';
import { UserInteractionEventProperties, EventName_UserInteraction, EventProperty_Value_ApplicationType, EventProperty_Value_WikiClick } from '../../store/models/customEvents';
import { useUserContext } from '../../Contexts/UserContext';

const useStyles = makeStyles({
    footerContainer: {
        display: 'flex',
        flexDirection: 'row',
        alignItems: 'center',
        justifyContent: 'space-between',
        '@media (max-width: 768px)': {
            // Completely break out of parent container constraints
            position: 'relative',
            left: '50%',
            right: '50%',
            marginLeft: '-50vw',
            marginRight: '-50vw',
            width: '100vw',
            paddingLeft: '0',
            paddingRight: '0',
        },
    },
    footerCentral: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        // Desktop margins
        ...shorthands.margin('0', '32px'),
        '@media (max-width: 768px)': {
            // Force zero margins on mobile and use full width
            ...shorthands.margin('0', '0px'),
            ...shorthands.padding('0', '4px'),
            width: '100%',
            boxSizing: 'border-box',
        },
    },
    footerLateral: {
        minWidth: '100px',
    },
    footerLateralLeft: {
        minWidth: '100px',
        '@media (max-width: 768px)': {
            // Hide left lateral space on mobile
            minWidth: '0px',
            width: '0px',
            flex: '0 0 auto',
        },
    },
    footerLateralRight: {
        minWidth: '100px',
        '@media (max-width: 768px)': {
            // Ultra-minimal width to fit version button on mobile
            minWidth: '0px',
            width: 'auto',
            flex: '0 0 auto',
            paddingLeft: '2px', // Minimal padding for separation
            '& button': {
                minWidth: '0px',
                padding: '4px 6px', // Compact button padding
                fontSize: '0.8rem', // Slightly smaller font
            },
        },
    },
    helpButton: {
        backgroundColor: tokens.colorNeutralForeground1,
    },
    // Show desktop message by default, hide on mobile
    desktopMessage: {
        display: 'block',
        '@media (max-width: 768px)': {
            display: 'none',
        },
    },
    // Hide mobile message by default, show only on mobile
    mobileMessage: {
        display: 'none',
        '@media (max-width: 768px)': {
            display: 'block',
            // Force text to use minimal margins only
            margin: '0',
            padding: '0 2px',
            width: '100%',
            textAlign: 'center',
            boxSizing: 'border-box',
        },
    },
});

export const Footer: FC = () => {
    const { t } = useTranslation();
    const { userProfile } = useUserContext();
    const classes = useStyles();

    const onVersionNumberClick = () => {
        appInsights.trackEvent({
            name: EventName_UserInteraction,
            properties: {
                applicationType: EventProperty_Value_ApplicationType,
                userId: userProfile?.username,
                interactionType: EventProperty_Value_WikiClick,
            } as UserInteractionEventProperties,
        });
        window.open('https://wiki.syensqo.com/pages/viewpage.action?pageId=899157532', '_blank');
    };
    return (
        <div className={classes.footerContainer}>
            <div className={classes.footerLateralLeft} />
            <div className={classes.footerCentral}>
                {/* Desktop message - full disclaimer */}
                <Text className={classes.desktopMessage}>{t('contentDisclaimer')}</Text>
                {/* Mobile message - shorter version */}
                <Text className={classes.mobileMessage}>{t('contentDisclaimerMobile')}</Text>
            </div>
            <div className={classes.footerLateralRight}>
                <Tooltip content={'Click to know more'} relationship='label'>
                    <Button appearance='transparent' size='large' onClick={onVersionNumberClick}>
                        v.{t('version')}
                    </Button>
                </Tooltip>
            </div>
        </div>
    );
};
